import { create } from 'zustand';
import { storage, STORAGE_KEYS } from '@utils/storage';
import { cartService } from '@services/cartService';
import type { Cart, CartItem, Restaurant } from '@/types';

let syncFromBackendInFlight: Promise<void> | null = null;

interface CartState extends Cart {
  isLoading: boolean;
  isSyncing: boolean;
  error: string | null;

  setRestaurant: (restaurant: Restaurant) => void;
  setServiceZone: (serviceZoneId: string) => Promise<void>;
  addItem: (item: CartItem) => Promise<void>;
  updateItem: (backendItemId: string, quantity: number) => Promise<void>;
  removeItem: (backendItemId: string) => Promise<void>;
  clearCart: () => Promise<void>;
  syncFromBackend: () => Promise<void>;
  resetToEmpty: () => void;

  _updateStateFromBackend: (backendCart: any, restaurant?: Restaurant) => void;
  _calculateTotals: () => void;
}

const initialState: Omit<CartState, 'setRestaurant' | 'setServiceZone' | 'addItem' | 'updateItem' | 'removeItem' | 'clearCart' | 'syncFromBackend' | 'resetToEmpty' | '_updateStateFromBackend' | '_calculateTotals'> = {
  restaurantId: undefined,
  serviceZoneId: undefined,
  items: [],
  subtotal: 0,
  deliveryFee: 0,
  serviceFee: 0,
  promotionDiscount: null,
  appliedPromotionName: null,
  total: 0,
  restaurant: undefined,
  isLoading: false,
  isSyncing: false,
  error: null,
};

const SERVICE_FEE_RATE = 0.05; // 5%

export const useCartStore = create<CartState>((set, get) => ({
  ...initialState,

  setRestaurant: (restaurant: Restaurant) => {
    const currentRestaurantId = get().restaurantId;
    const currentItems = get().items;

    const cartHasItems = currentItems.length > 0;
    const cartIsBoundToDifferentRestaurant =
      cartHasItems && currentRestaurantId && currentRestaurantId !== restaurant.id;

    if (cartIsBoundToDifferentRestaurant) {
      return;
    }

    set({
      restaurantId: restaurant.id,
      serviceZoneId: restaurant.serviceZoneId,
      restaurant,
    });
  },

  /**
   * Set service zone (syncs with backend)
   */
  setServiceZone: async (serviceZoneId: string) => {
    set({ isSyncing: true, error: null });
    try {
      const backendCart = await cartService.setServiceZone(serviceZoneId);
      get()._updateStateFromBackend(backendCart);
    } catch (err: any) {
      const errorMessage = err.response?.data?.error?.message || 'Service zone ayarlanırken bir hata oluştu.';
      set({ error: errorMessage });
      throw err;
    } finally {
      set({ isSyncing: false });
    }
  },

  /**
   * Add item to cart (backend API)
   */
  addItem: async (item: CartItem) => {
    set({ isSyncing: true, error: null });
    try {
      const backendCart = await cartService.addItem(item);
      get()._updateStateFromBackend(backendCart);
    } catch (err: any) {
      const errorMessage = err.response?.data?.error?.message || 'Ürün sepete eklenirken bir hata oluştu.';
      set({ error: errorMessage });
      throw err;
    } finally {
      set({ isSyncing: false });
    }
  },

  /**
   * Update cart item quantity (backend API)
   * 
   * NOTE: Uses backend item ID (not frontend itemKey)
   */
  updateItem: async (backendItemId: string, quantity: number) => {
    if (quantity < 1) {
      // If quantity would be 0, remove item instead
      await get().removeItem(backendItemId);
      return;
    }

    set({ isSyncing: true, error: null });
    try {
      const backendCart = await cartService.updateItem(backendItemId, quantity);
      get()._updateStateFromBackend(backendCart);
    } catch (err: any) {
      const errorMessage = err.response?.data?.error?.message || 'Ürün güncellenirken bir hata oluştu.';
      set({ error: errorMessage });
      throw err;
    } finally {
      set({ isSyncing: false });
    }
  },

  /**
   * Remove item from cart (backend API)
   * 
   * NOTE: Uses backend item ID (not frontend itemKey)
   * If item not found (404), syncs cart from backend (item may have been removed already)
   */
  removeItem: async (backendItemId: string) => {
    set({ isSyncing: true, error: null });
    try {
      const backendCart = await cartService.removeItem(backendItemId);
      get()._updateStateFromBackend(backendCart);
    } catch (err: any) {
      // If item not found (404), item may have been removed already or cart was reset
      // Sync cart from backend to get current state
      if (err.response?.status === 404) {
        try {
          const backendCart = await cartService.getCart();
          get()._updateStateFromBackend(backendCart);
          // Don't throw error if sync succeeds - item was likely already removed
          return;
        } catch (syncErr) {
          console.error('Failed to sync cart after remove error:', syncErr);
        }
      }
      const errorMessage = err.response?.data?.error?.message || 'Ürün sepetten çıkarılırken bir hata oluştu.';
      set({ error: errorMessage });
      throw err;
    } finally {
      set({ isSyncing: false });
    }
  },

  /**
   * Clear cart (backend API)
   */
  clearCart: async () => {
    set({ isSyncing: true, error: null });
    try {
      const backendCart = await cartService.clearCart();
      get()._updateStateFromBackend(backendCart);
      storage.remove(STORAGE_KEYS.GUEST_CART);
    } catch (err: any) {
      const errorMessage = err.response?.data?.error?.message || 'Sepet temizlenirken bir hata oluştu.';
      set({ error: errorMessage });
      throw err;
    } finally {
      set({ isSyncing: false });
    }
  },

  resetToEmpty: () => {
    set((prev) => ({ ...prev, ...initialState }));
  },

  syncFromBackend: async () => {
    if (syncFromBackendInFlight) {
      return syncFromBackendInFlight;
    }

    set({ isLoading: true, error: null });
    syncFromBackendInFlight = (async () => {
      try {
        const backendCart = await cartService.getCart();
        get()._updateStateFromBackend(backendCart);
      } catch (err: any) {
        const status = err.response?.status;
        const hasLocalItems = get().items.length > 0;
        if ((status === 401 || status === 403) && !hasLocalItems) {
          get().resetToEmpty();
        } else if (status !== 401 && status !== 403) {
          const errorMessage = err.response?.data?.error?.message || 'Sepet yüklenirken bir hata oluştu.';
          set({ error: errorMessage });
        }
        console.error('Failed to sync cart from backend:', err);
      } finally {
        set({ isLoading: false });
        syncFromBackendInFlight = null;
      }
    })();

    return syncFromBackendInFlight;
  },

  /**
   * Internal: Update state from backend cart response
   * 
   * Maps backend cart format to frontend format
   * Preserves restaurant metadata (backend cart doesn't store this)
   */
  _updateStateFromBackend: (backendCart: any, restaurant?: Restaurant) => {
    if (!backendCart || !Array.isArray(backendCart.items)) {
      set({
        items: [],
        restaurantId: undefined,
        serviceZoneId: backendCart?.serviceZoneId ?? undefined,
        subtotal: 0,
        deliveryFee: 0,
        promotionDiscount: null,
        appliedPromotionName: null,
        serviceFee: 0,
        total: 0,
      });
      return;
    }

    const currentRestaurant = restaurant || get().restaurant;

    const items: CartItem[] = backendCart.items.map((backendItem: any) => ({
      menuItemId: backendItem.productId,
      quantity: backendItem.quantity,
      selectedOptions: (Array.isArray(backendItem.optionIds) ? backendItem.optionIds : []).map((optionId: string) => ({
        optionGroupId: '', // Backend doesn't store optionGroupId - limitation
        optionId,
      })),
      backendItemId: backendItem.id, // Store backend ID for update/remove operations
      itemTotal: backendItem.unitPrice || 0, // Already in TRY
      // Backend denormalized data for display
      productName: backendItem.productName,
      productImage: backendItem.productImage,
      optionNames: backendItem.optionNames || [],
      specialInstructions: backendItem.specialInstructions || undefined,
      removedIngredientIds: backendItem.removedIngredientIds || [],
      removedIngredientNames: backendItem.removedIngredientNames || [],
    }));

    const restaurantId = backendCart.restaurantId || undefined;

    const serviceZoneId = backendCart.serviceZoneId || undefined;

    // CRITICAL: Backend sends prices in TRY (not cents), use directly
    const subtotal = backendCart.cartTotal || 0;
    
    // CRITICAL: Use backend-calculated deliveryFee from ServiceZone (geo-based model)
    // DO NOT use restaurant.deliveryFee (deprecated/legacy)
    // Already in TRY (not cents)
    const deliveryFee = backendCart.deliveryFee !== null && backendCart.deliveryFee !== undefined
      ? backendCart.deliveryFee
      : 0;

    const promotionDiscount = backendCart.promotionDiscount !== null && backendCart.promotionDiscount !== undefined
      ? backendCart.promotionDiscount
      : null;
    
    const appliedPromotionName = backendCart.appliedPromotionName || null;
    
    const serviceFee = Math.round(subtotal * SERVICE_FEE_RATE * 100) / 100;
    const total = Math.round((subtotal + deliveryFee + serviceFee - (promotionDiscount || 0)) * 100) / 100;

    set({
      items,
      restaurantId,
      serviceZoneId,
      subtotal,
      deliveryFee,
      promotionDiscount,
      appliedPromotionName,
      serviceFee,
      total,
    });
  },

  /**
   * Internal: Calculate totals (legacy, kept for compatibility)
   * 
   * NOTE: Totals are now calculated from backend cartTotal in _updateStateFromBackend
   * This method is kept for backward compatibility but shouldn't be needed
   */
  _calculateTotals: () => {
    const items = get().items;
    const restaurant = get().restaurant;

    const subtotal = items.reduce((sum, item) => {
      const itemPrice = item.itemTotal || 0;
      return sum + itemPrice * item.quantity;
    }, 0);

    // CRITICAL: Use backend-calculated deliveryFee, NOT restaurant.deliveryFee (deprecated)
    // This legacy method should not be used - totals come from backend
    const deliveryFee = 0;
    const serviceFee = Math.round(subtotal * SERVICE_FEE_RATE * 100) / 100;
    const total = Math.round((subtotal + deliveryFee + serviceFee) * 100) / 100;

    set({
      subtotal,
      deliveryFee,
      serviceFee,
      total,
    });
  },
}));

/**
 * Generate unique key for cart item (legacy, for backward compatibility)
 * 
 * NOTE: With backend integration, we now use backendItemId for update/remove operations
 * This function is kept for display purposes (React keys, etc.)
 */
export const getCartItemKey = (item: CartItem): string => {
  // Prefer backendItemId if available (more reliable)
  if (item.backendItemId) {
    return item.backendItemId;
  }
  
  // Fallback to composite key (legacy)
  const optionsKey = JSON.stringify(item.selectedOptions.sort((a, b) => {
    if (a.optionGroupId !== b.optionGroupId) {
      return a.optionGroupId.localeCompare(b.optionGroupId);
    }
    return a.optionId.localeCompare(b.optionId);
  }));
  const removedKey = JSON.stringify([...(item.removedIngredientIds || [])].sort());
  return `${item.menuItemId}-${optionsKey}-${removedKey}`;
};
